import React, { useState, useEffect } from 'react';
import { Container, Typography, Grid, Box, Button, Alert, Breadcrumbs, Link } from '@mui/material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import ProductCard from '../components/ProductCard';
import Loading from '../components/Loading';
import { productService, categoryService } from '../services/api';

const CategoryProducts = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [productsResponse, categoryResponse] = await Promise.all([
          productService.getByCategory(categoryId),
          categoryService.getById(categoryId),
        ]);
        setProducts(productsResponse.data);
        setCategory(categoryResponse.data);
        setError(null);
      } catch (err) {
        setError('Impossible de charger les produits de cette catégorie.');
        console.error('Error fetching category products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [categoryId]);

  if (loading) return <Loading />;

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      <Breadcrumbs sx={{ mb: { xs: 2, sm: 3 }, px: { xs: 1, sm: 0 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
        <Link component={RouterLink} to="/" underline="hover" color="inherit">Accueil</Link>
        <Link component={RouterLink} to="/categories" underline="hover" color="inherit">Catégories</Link>
        <Typography color="text.primary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          {category?.titre || 'Catégorie'}
        </Typography>
      </Breadcrumbs>

      <Button 
        startIcon={<FaArrowLeft />} 
        onClick={() => navigate('/categories')} 
        sx={{ mb: { xs: 2, sm: 3 }, px: { xs: 1, sm: 2 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}
      >
        Retour aux catégories
      </Button>

      <Typography 
        variant="h2" 
        component="h1" 
        gutterBottom 
        sx={{ 
          mb: { xs: 2, sm: 3, md: 4 },
          fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
        }}
      >
        {category?.titre || 'Produits'}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3, mx: { xs: 1, sm: 0 } }}>{error}</Alert>}

      {products.length === 0 && !error ? (
        <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6, md: 8 } }}>
          <Typography 
            variant="h6" 
            color="text.secondary"
            sx={{ 
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              px: { xs: 2, sm: 0 },
            }}
          >
            Aucun produit disponible dans cette catégorie pour le moment.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          {products.map((product) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
              <ProductCard product={product} />
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default CategoryProducts;
